/*
 * useFleetPlanLayer
 *
 * Owns the fleet-plan overview: its on/off switch and toggle, the fetch of the
 * latest active plan route per vessel (/routes/fleet-plans + waypoints), and
 * the plan routes drawn on the map (tree-selected plus the fleet overview).
 * The overview is a fleet-level view: while a vessel is marked it is not
 * drawn (the selection owns the map), but stays fetched so unmarking brings it
 * straight back. The plan layer is visible iff that set is non-empty.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { coreGet } from "@/services/coreApi.jsx";
import { useRouteStore } from "@/features/route/state/RouteStore.jsx";
import {
  normalizeCoreWaypoints,
  selectRenderableRoutes,
} from "@/features/route/state/index.jsx";
import { buildVesselLabels } from "@/utils/vesselHelper.jsx";

// The small fleet-plans list loads whenever the panel is open (card counts),
// the large waypoints only while the layer is active.
function useFleetPlanRoutes({
  enabled,
  active,
  accessContext,
  setAccessContext,
  refreshNonce,
}) {
  const [fleetPlans, setFleetPlans] = useState([]);
  const [routeIds, setRouteIds] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingWaypoints, setLoadingWaypoints] = useState(false);
  const { state: routeStoreState, replaceRoutes } = useRouteStore();

  const listWanted = enabled || active;
  useEffect(() => {
    if (!listWanted) return undefined;
    let cancelled = false;
    const fetchList = async () => {
      setLoadingList(true);
      try {
        const data = await coreGet(
          "/routes/fleet-plans",
          accessContext,
          setAccessContext,
          ".core /routes/fleet-plans error=",
        );
        if (cancelled) return;
        setFleetPlans(Array.isArray(data?.routes) ? data.routes : []);
      } catch (err) {
        if (cancelled) return;
        setFleetPlans([]);
        console.error("Fleet plan routes fetch failed:", err);
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    };
    fetchList();
    return () => {
      cancelled = true;
      setLoadingList(false);
    };
  }, [listWanted, accessContext, setAccessContext, refreshNonce]);

  const fleetRouteIds = useMemo(
    () =>
      Array.from(
        new Set(
          fleetPlans
            .map((route) => Number(route?.id))
            .filter((id) => Number.isFinite(id)),
        ),
      ),
    [fleetPlans],
  );

  useEffect(() => {
    if (!active || fleetRouteIds.length === 0) {
      setRouteIds([]);
      return undefined;
    }
    let cancelled = false;
    const fetchWaypoints = async () => {
      setLoadingWaypoints(true);
      try {
        const params = new URLSearchParams();
        fleetRouteIds.forEach((id) => params.append("route_ids", String(id)));
        const waypointData = await coreGet(
          `/waypoints?${params.toString()}`,
          accessContext,
          setAccessContext,
          ".core /waypoints (fleet plans) error=",
        );
        if (cancelled) return;
        const fetched = Array.isArray(waypointData?.waypoints)
          ? waypointData.waypoints
          : [];
        const fragment = normalizeCoreWaypoints(fetched);
        // Fetched routes that returned no waypoints must still be emptied.
        for (const id of fleetRouteIds) {
          if (!fragment.waypointsByRouteId[id]) {
            fragment.waypointsByRouteId[id] = [];
          }
        }
        replaceRoutes(fragment);
        setRouteIds(fleetRouteIds);
      } catch (err) {
        if (cancelled) return;
        setRouteIds([]);
        console.error("Fleet plan waypoints fetch failed:", err);
      } finally {
        if (!cancelled) setLoadingWaypoints(false);
      }
    };
    fetchWaypoints();
    return () => {
      cancelled = true;
      setLoadingWaypoints(false);
    };
  }, [active, fleetRouteIds, accessContext, setAccessContext, replaceRoutes]);

  const fleetPlanRoutes = useMemo(() => {
    if (!active || routeIds.length === 0) return [];
    return selectRenderableRoutes(routeStoreState, routeIds);
  }, [active, routeIds, routeStoreState]);

  const summary = useMemo(() => {
    const routeByVesselId = {};
    for (const route of fleetPlans) {
      const vesselId = Number(route?.vesselId);
      const id = Number(route?.id);
      if (!Number.isFinite(vesselId) || !Number.isFinite(id)) continue;
      routeByVesselId[vesselId] = { id, voyageId: Number(route?.voyageId) };
    }
    return {
      routeCount: fleetRouteIds.length,
      vesselCount: Object.keys(routeByVesselId).length,
      routeByVesselId,
      loading: loadingList,
    };
  }, [fleetPlans, fleetRouteIds, loadingList]);

  return { fleetPlanRoutes, loading: loadingList || loadingWaypoints, summary };
}

export default function useFleetPlanLayer({
  enabled = false,
  vesselPlanRoutes,
  markedVesselNames,
  setSwitchVesselFiltersActive,
  accessContext,
  setAccessContext,
  refreshNonce,
}) {
  const [switchFleetPlan, setSwitchFleetPlan] = useState(false);

  const {
    fleetPlanRoutes,
    loading: fetchingFleetPlan,
    summary: fleetPlanSummary,
  } = useFleetPlanRoutes({
    enabled,
    active: switchFleetPlan,
    accessContext,
    setAccessContext,
    refreshNonce,
  });

  const handleToggleFleetPlan = useCallback(() => {
    const next = !switchFleetPlan;
    setSwitchFleetPlan(next);
    if (next) setSwitchVesselFiltersActive(true);
  }, [switchFleetPlan, setSwitchVesselFiltersActive]);

  const displayedPlanRoutes = useMemo(() => {
    const base = vesselPlanRoutes ?? [];
    if (
      !switchFleetPlan ||
      (fleetPlanRoutes?.length ?? 0) === 0 ||
      (markedVesselNames?.length ?? 0) > 0
    ) {
      return base;
    }
    const seenRouteIds = new Set(base.map((route) => route?.points?.routeId));
    return [
      ...base,
      ...fleetPlanRoutes.filter(
        (route) => !seenRouteIds.has(route?.points?.routeId),
      ),
    ];
  }, [vesselPlanRoutes, switchFleetPlan, fleetPlanRoutes, markedVesselNames]);

  // Disambiguates same vessel/state sibling routes for the vessel markers.
  const vesselLabelMap = useMemo(
    () => buildVesselLabels(displayedPlanRoutes),
    [displayedPlanRoutes],
  );

  return {
    switchFleetPlan,
    fetchingFleetPlan,
    fleetPlanSummary,
    handleToggleFleetPlan,
    displayedPlanRoutes,
    vesselLabelMap,
    planLayerVisible: displayedPlanRoutes.length > 0,
  };
}
